import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ProfileRow {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  email?: string | null;
  [key: string]: unknown;
}

/**
 * Hook compartilhado que busca o perfil do usuário autenticado.
 * Centraliza o `useQuery(['profile', user?.id])` que estava duplicado em
 * AppLayout, AppSidebar e todas as sidebars de módulo (ENEM/OAB/Videoaulas/Module).
 * React Query deduplica pela mesma queryKey, então múltiplos consumidores
 * compartilham o mesmo cache.
 */
export function useProfile() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();
      return (data as ProfileRow | null) ?? null;
    },
    enabled: !!user?.id,
    staleTime: 60_000,
  });

  const profile = query.data ?? null;
  const userInitials =
    profile?.first_name && profile?.last_name
      ? `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase()
      : (user?.email?.[0] || "U").toUpperCase();
  const displayName =
    profile?.first_name || user?.email?.split("@")[0] || "Usuário";

  return {
    profile,
    userInitials,
    displayName,
    isLoading: query.isLoading,
  };
}
